'use client';

import { Anchor, Popover, type PopoverProps } from '@mantine/core';
import { useDisclosure, useHover } from '@mantine/hooks';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import styles from './PopoverDefinition.module.scss';

interface PopoverDefinitionProps extends Omit<PopoverProps, 'children'> {
	children: ReactNode;
	content?: ReactNode;
	showDelay?: number;
	hideDelay?: number;
}
export function PopoverDefinition({
	children,
	content,
	showDelay = 150,
	hideDelay = 50,
	...popoverProps
}: PopoverDefinitionProps): ReactNode {
	const [opened, { close, open }] = useDisclosure(false);
	const showTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
	const hideTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
	const { hovered, ref } = useHover<HTMLAnchorElement>();

	// Drive open/close with delays whenever hover state changes
	useEffect(() => {
		if (hovered) {
			clearTimeout(hideTimeoutRef.current);
			if (showDelay > 0) {
				showTimeoutRef.current = setTimeout(open, showDelay);
			} else {
				open();
			}
		} else {
			clearTimeout(showTimeoutRef.current);
			if (hideDelay > 0) {
				hideTimeoutRef.current = setTimeout(close, hideDelay);
			} else {
				close();
			}
		}

		return () => {
			clearTimeout(showTimeoutRef.current);
			clearTimeout(hideTimeoutRef.current);
		};
	}, [hovered, showDelay, hideDelay, open, close]);

	// If we click on the anchor while the popover is open, close it immediately (without delay)
	const handleClick = useCallback(() => {
		clearTimeout(showTimeoutRef.current);
		clearTimeout(hideTimeoutRef.current);
		if (opened) {
			close();
		}
	}, [opened, close]);

	// If no popup content, just render the children
	if (!content) {
		return children;
	}

	return (
		// Look at changing this into a Tooltip, since intercepting click events as a real Anchor could cause problems with tooltips in radio button labels
		<Popover {...popoverProps} closeOnClickOutside={true} opened={opened}>
			<Popover.Target>
				<Anchor ref={ref} onClick={handleClick} className={styles['link']}>
					{children}
				</Anchor>
			</Popover.Target>
			<Popover.Dropdown className={styles['popover']}>
				<div className={styles['popoverContent']}>{content}</div>
			</Popover.Dropdown>
		</Popover>
	);
}
